
import React from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Brain, X, Loader2 } from "lucide-react";
import { AiAnalysisSession } from '@/services/AiMarketAnalysisService';

interface AiAnalysisProgressProps {
  session: AiAnalysisSession;
  onCancel: () => void;
}

const stepNames = {
  1: "AI Recommendation",
  2: "Trends Analysis", 
  3: "Key Levels",
  4: "Price Targets",
  5: "Analysis Summary"
};

export const AiAnalysisProgress = ({ session, onCancel }: AiAnalysisProgressProps) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'COMPLETED': return 'bg-green-500/20 text-green-700 border-green-500/30';
      case 'PROCESSING': 
      case 'IN_PROGRESS': return 'bg-blue-500/20 text-blue-700 border-blue-500/30';
      case 'FAILED': return 'bg-red-500/20 text-red-700 border-red-500/30';
      case 'RATE_LIMITED': return 'bg-orange-500/20 text-orange-700 border-orange-500/30';
      default: return 'bg-yellow-500/20 text-yellow-700 border-yellow-500/30';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'COMPLETED': return 'Completed';
      case 'PROCESSING': return 'Processing';
      case 'IN_PROGRESS': return 'In Progress';
      case 'FAILED': return 'Failed';
      case 'RATE_LIMITED': return 'Rate Limited';
      default: return status;
    }
  };

  const getCurrentStepName = () => {
    return stepNames[session.current_step as keyof typeof stepNames] || 'Analysis';
  };

  return (
    <Card className="relative overflow-hidden bg-gradient-to-br from-background via-background to-blue-500/5 border border-border/50 shadow-lg animate-fade-in">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-500/10">
              <Brain className="h-5 w-5 text-blue-500" />
            </div>
            <div>
              <h3 className="font-semibold text-foreground">AI Analysis - {session.symbol}</h3>
              <p className="text-sm text-muted-foreground">
                {getCurrentStepName()} • Step {session.current_step} of {session.total_steps}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge className={getStatusColor(session.status)}>
              <Loader2 className="h-3 w-3 mr-1 animate-spin" />
              {getStatusText(session.status)}
            </Badge>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={onCancel}
              className="h-8 w-8 p-0 hover:bg-red-500/10"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Progress</span>
            <span className="font-medium">{session.progress_percentage}%</span>
          </div>
          <Progress value={session.progress_percentage} className="h-2" />
          
          {session.status_message && (
            <p className="text-sm text-muted-foreground italic mt-2">
              {session.status_message}
            </p>
          )}
        </div>

        {session.error_message && (
          <div className="mt-3 p-2 bg-red-500/10 border border-red-500/20 rounded-lg">
            <p className="text-sm text-red-700">{session.error_message}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
